import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, UserPlus, AlertTriangle } from 'lucide-react';
import useAuth from '../../../hooks/useAuth';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '../../ui/card';
import Input from '../../ui/input';
import { Button } from '../../ui/button';
import Checkbox from '../../ui/checkbox';
import { ScaleTransition } from '../../ui/transitions';
import TwoFactorVerify from '../2fa/TwoFactorVerify';
import { Alert, AlertDescription } from '../../ui/alert';

/**
 * Renders a login form with username and password inputs, password visibility toggle,
 * error handling, and a link to the signup page.
 */
const Login = () => {
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/home';

  const {
    username,
    password,
    showPassword,
    err,
    handleSubmit,
    handleInputChange,
    togglePasswordVisibility,
    requires2FA,
    pending2FAUsername,
    setRequires2FA,
    completeLoginWith2FA,
    isLoading,
    isGuestLoading,
    handleGuest,
    handleProviderSignIn,
  } = useAuth('login', returnUrl);

  // Check if the error is a ban message
  const isBanError = err && err.toLowerCase().includes('account has been banned');

  const handleGoogleSignIn = () => handleProviderSignIn('google');
  const handleGithubSignIn = () => handleProviderSignIn('github');

  const handleCancel2FA = () => {
    setRequires2FA(false);
  };

  return (
    <div className='container flex items-center justify-center min-h-screen'>
      {requires2FA ? (
        <TwoFactorVerify
          username={pending2FAUsername}
          onSuccess={completeLoginWith2FA}
          onCancel={handleCancel2FA}
        />
      ) : (
        <ScaleTransition duration={0.4}>
          <Card className='w-[400px] shadow-lg'>
            <CardHeader>
              <CardTitle>Welcome to Syntax Error!</CardTitle>
              <CardDescription>Please login to continue.</CardDescription>
            </CardHeader>
            <CardContent>
              {isBanError ? (
                <Alert variant='destructive' className='mb-4 border-red-800 bg-red-50'>
                  <AlertTriangle className='h-4 w-4' />
                  <div className='font-bold text-base'>Account Banned</div>
                  <AlertDescription className='mt-2'>{err}</AlertDescription>
                </Alert>
              ) : null}
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <label htmlFor='username-input' className='text-sm font-medium'>
                    Username
                  </label>
                  <Input
                    type='text'
                    id='username-input'
                    value={username}
                    onChange={event => handleInputChange(event, 'username')}
                    placeholder='Enter your username'
                    required
                    className='transition-all duration-200 focus:ring-2 focus:ring-blue-500'
                  />
                </div>
                <div className='space-y-2'>
                  <label htmlFor='password-input' className='text-sm font-medium'>
                    Password
                  </label>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    id='password-input'
                    value={password}
                    onChange={event => handleInputChange(event, 'password')}
                    placeholder='Enter your password'
                    required
                    className='transition-all duration-200 focus:ring-2 focus:ring-blue-500'
                  />
                </div>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='showPasswordToggle'
                    checked={showPassword}
                    onCheckedChange={togglePasswordVisibility}
                  />
                  <label
                    htmlFor='showPasswordToggle'
                    className='text-sm text-muted-foreground cursor-pointer'>
                    Show Password
                  </label>
                </div>
                {err && !isBanError && (
                  <div className='animate-in slide-in-from-top fade-in duration-200'>
                    <p className='text-sm text-destructive'>{err}</p>
                  </div>
                )}
                <Button
                  type='submit'
                  className='w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]'
                  disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>
                <div className='relative my-4'>
                  <div className='absolute inset-0 flex items-center'>
                    <span className='w-full border-t' />
                  </div>
                  <div className='relative flex justify-center text-xs uppercase'>
                    <span className='bg-background px-2 text-muted-foreground'>
                      Or continue with
                    </span>
                  </div>
                </div>
                <div className='space-y-2'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleGoogleSignIn}
                    className='w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]'
                    disabled={isLoading}>
                    <svg className='mr-2 h-4 w-4' viewBox='0 0 24 24'>
                      <path
                        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                        fill='#4285F4'
                      />
                      <path
                        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                        fill='#34A853'
                      />
                      <path
                        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                        fill='#FBBC05'
                      />
                      <path
                        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                        fill='#EA4335'
                      />
                    </svg>
                    Sign in with Google
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleGithubSignIn}
                    className='w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]'
                    disabled={isLoading}>
                    <svg className='mr-2 h-4 w-4' viewBox='0 0 24 24'>
                      <path
                        d='M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12c0-5.523-4.477-10-10-10z'
                        fill='currentColor'
                      />
                    </svg>
                    Sign in with GitHub
                  </Button>
                </div>
                <Button
                  type='button'
                  variant='outline'
                  onClick={handleGuest}
                  className='w-full transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]'
                  disabled={isLoading || isGuestLoading}>
                  {isGuestLoading ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Entering as guest...
                    </>
                  ) : (
                    <>
                      <UserPlus className='mr-2 h-4 w-4' />
                      Guest
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
            <CardFooter className='flex justify-center'>
              <Link
                to='/signup'
                className='text-sm text-primary hover:underline transition-colors duration-200'>
                Don&apos;t have an account? Sign up here.
              </Link>
            </CardFooter>
          </Card>
        </ScaleTransition>
      )}
    </div>
  );
};

export default Login;
